import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ClipboardList } from 'lucide-react';
import { formatMeters } from '../data/catalog';

const SolicitationContext = createContext(null);

const STORAGE_KEY = 'hud_solicitacao_v2';

export function SolicitationProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    } catch {
      return [];
    }
  });
  const [added, setAdded] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const addItem = useCallback((product, variant, meters) => {
    // Produto sem variantes: variant = null — o item é o próprio produto (sem nome fictício).
    const key = variant ? `${product.slug}::${variant.id}` : product.slug;
    setItems((prev) => {
      const existing = prev.find((i) => i.key === key);
      if (existing) {
        return prev.map((i) =>
          i.key === key ? { ...i, meters: Math.min(i.meters + meters, 999.99) } : i
        );
      }
      return [
        ...prev,
        {
          key,
          productSlug: product.slug,
          name: product.name,
          categorySlug: product.categorySlug,
          variantId: variant?.id || null,
          variantName: variant?.name || null,
          image: variant?.image || product.coverImage || product.image || null,
          meters,
        },
      ];
    });
    setAdded({ product, variant, meters });
  }, []);

  // Pedido sob medida dentro do produto: o cliente não achou a cor/estampa e descreve
  // o que procura. O texto entra no lugar da variante; a metragem é opcional.
  const addCustomItem = useCallback((product, note, meters) => {
    const key = `${product.slug}::pedido-${Date.now()}`;
    setItems((prev) => [
      ...prev,
      {
        key,
        productSlug: product.slug,
        name: product.name,
        categorySlug: product.categorySlug,
        variantId: null,
        variantName: note,
        customRequest: true,
        image: product.coverImage || product.image || null,
        meters: meters ?? null,
      },
    ]);
    setAdded({ product, variant: { name: note }, meters: meters ?? null });
  }, []);

  const updateMeters = useCallback((key, meters) => {
    setItems((prev) => prev.map((i) => (i.key === key ? { ...i, meters } : i)));
  }, []);

  const removeItem = useCallback((key) => {
    setItems((prev) => prev.filter((i) => i.key !== key));
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const count = items.length;

  return (
    <SolicitationContext.Provider
      value={{ items, addItem, addCustomItem, updateMeters, removeItem, clear, count }}
    >
      {children}
      {added && (
        <div
          className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center bg-black/40 p-4"
          role="dialog"
          aria-modal="true"
          data-testid="added-to-request-modal"
          onClick={() => setAdded(null)}
        >
          <div
            className="w-full max-w-md bg-white rounded-2xl p-6 shadow-xl animate-fade-up"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-4">
              {(added.variant?.image || added.product.coverImage || added.product.image) ? (
                <img
                  src={added.variant?.image || added.product.coverImage || added.product.image}
                  alt={added.variant ? `${added.product.name} - ${added.variant.name}` : added.product.name}
                  className="w-16 h-16 rounded-lg object-cover"
                />
              ) : (
                <div className="flex h-16 w-16 items-center justify-center rounded-lg bg-navy-light text-navy/40">
                  <ClipboardList className="h-6 w-6" />
                </div>
              )}
              <div>
                <p className="font-serif text-lg text-navy">Adicionado à sua solicitação</p>
                <p className="text-sm text-muted-foreground">
                  {added.product.name}
                  {added.variant ? ` · ${added.variant.name}` : ''} · {added.meters ? formatMeters(added.meters) : 'metragem a confirmar'}
                </p>
              </div>
            </div>
            <div className="mt-6 grid gap-3">
              <button
                data-testid="modal-view-request-button"
                onClick={() => {
                  setAdded(null);
                  navigate('/solicitacao');
                }}
                className="w-full rounded-full bg-navy px-6 py-3 text-sm font-semibold tracking-wide text-white transition-colors hover:bg-navy-hover"
              >
                VER MINHA SOLICITAÇÃO
              </button>
              <button
                data-testid="modal-continue-shopping-button"
                onClick={() => setAdded(null)}
                className="w-full rounded-full border border-navy/20 px-6 py-3 text-sm font-semibold tracking-wide text-navy transition-colors hover:bg-navy-light"
              >
                CONTINUAR ESCOLHENDO
              </button>
            </div>
          </div>
        </div>
      )}
    </SolicitationContext.Provider>
  );
}

export const useSolicitation = () => useContext(SolicitationContext);
